import { useRef } from 'react';
import { motion, useMotionValue, useScroll, useSpring, useTransform } from 'framer-motion';
import { ArrowRight, Award } from 'lucide-react';
import { assetUrl } from '../data';
import { EASE, useApp, useSite } from './shared';

const lineReveal = (text, i, cls = '') => (
  <span key={i} className="block overflow-hidden pb-[0.09em] -mb-[0.09em]">
    <motion.span
      className={`block ${cls}`}
      initial={{ y: '115%' }}
      animate={{ y: '0%' }}
      transition={{ duration: 1.1, delay: 0.3 + i * 0.14, ease: EASE }}
    >
      {text}
    </motion.span>
  </span>
);

function OrbitBadge() {
  return (
    <div className="relative flex h-44 w-44 items-center justify-center">
      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full animate-spin-slow" aria-hidden="true">
        <defs>
          <path id="circ" d="M50,50 m-40,0 a40,40 0 1,1 80,0 a40,40 0 1,1 -80,0" />
        </defs>
        <text fill="rgba(255,255,255,0.78)" fontSize="6.6" fontFamily="JetBrains Mono, monospace" fontWeight="500">
          <textPath href="#circ" textLength="249" lengthAdjust="spacing">
            15 YEARS OF RECOGNISING EXCELLENCE ✦
          </textPath>
        </text>
      </svg>
      <div className="flex h-20 w-20 items-center justify-center rounded-full border border-lime/40 bg-ink/70 backdrop-blur-sm">
        <Award size={30} className="text-lime" strokeWidth={1.5} />
      </div>
    </div>
  );
}

export default function Hero() {
  const { openModal } = useApp();
  const { settings } = useSite();
  const secRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: secRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 55, damping: 14 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 55, damping: 14 });

  const onMouseMove = (e) => {
    const r = secRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="home"
      ref={secRef}
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen flex-col justify-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110" data-testid="hero-parallax-bg">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={`${process.env.PUBLIC_URL}/media/hero-poster.jpg`}
          className="h-full w-full object-cover opacity-60"
          data-testid="hero-bg-video"
        >
          <source src={`${process.env.PUBLIC_URL}/media/hero.mp4`} type="video/mp4" />
          <source src={`${process.env.PUBLIC_URL}/media/hero.webm`} type="video/webm" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/70 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-ink/50" />
      </motion.div>
      <div className="pointer-events-none absolute -left-32 top-1/3 h-[560px] w-[560px] rounded-full bg-lime/10 blur-[160px]" />

      <motion.div style={{ opacity: contentOpacity }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-36 md:px-8 md:pt-40">
        <div className="flex flex-col gap-12 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="font-display font-extrabold leading-[0.93] tracking-tight">
              <span className="sr-only">The CXO100 — New Delhi Feb 05, 2027 · Mumbai Feb 19, 2027</span>
              <span className="block overflow-hidden pb-2">
                <motion.img
                  src={assetUrl(settings.logo_event)}
                  alt=""
                  aria-hidden="true"
                  data-testid="hero-event-logo"
                  initial={{ y: '40%', opacity: 0, filter: 'blur(8px)' }}
                  animate={{ y: '0%', opacity: 1, filter: 'blur(0px)' }}
                  transition={{ duration: 1.2, delay: 0.25, ease: EASE }}
                  className="logo-on-dark block h-auto w-[min(92vw,640px)] select-none object-contain drop-shadow-[0_10px_40px_rgba(0,0,0,0.45)]"
                  draggable="false"
                />
              </span>
              <span className="mt-5 block text-[clamp(1.05rem,2.1vw,1.7rem)] font-bold italic">
                {lineReveal(<span className="text-stroke">Unleashing the Power of AI &amp; Cybersecurity for Business Resiliency</span>, 3)}
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
              className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
            >
              Keynotes, Power Panels, CXO Roundtables and the celebrated CXO100 Awards with India&apos;s top CIOs,
              CISOs, CDOs, DPOs, AI Leaders and Digital Transformation Champions.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.12, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-register-button"
                onClick={() => openModal('register')}
                className="group inline-flex items-center gap-3 rounded-full bg-lime px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-ink transition-all duration-300 hover:shadow-[0_0_40px_rgba(153,204,0,0.5)]"
              >
                Express Interest
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </button>

              <button
                data-testid="hero-partner-button"
                onClick={() => openModal('partner')}
                className="inline-flex items-center gap-3 rounded-full border border-lime/50 px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-lime transition-all duration-300 hover:bg-lime/10 hover:shadow-[0_0_28px_rgba(153,204,0,0.25)]"
              >
                Join as a Partner
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 1.1, ease: EASE }}
            style={{ rotateX, rotateY, transformPerspective: 900 }}
            className="hidden shrink-0 self-center md:block"
            data-testid="hero-orbit-badge"
          >
            <OrbitBadge />
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-2 font-mono text-[9px] uppercase tracking-[0.4em] text-slate-500">
          Scroll
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="h-8 w-px bg-gradient-to-b from-lime to-transparent" />
        </div>
      </motion.div>
    </section>
  );
}
